import OrderDTO from "@/dto/order/OrderDTO";
import type GlobalStore from "@/store";
import { ShipValidationResult, ShipValidationRule, ShipValidator } from "./type";
import { buildValidationFailedResult, buildValidationSuccessfulResult } from "./util";
import validatePrintShippingDisabled from "./rule/validatePrintShippingDisabled";
import validateAutoPrint from "./rule/validateAutoPrint";
import { validateRefundingOrder } from "./rule/validateRefundingOrder";
import validateJdMaxEwaybill from "./rule/validateJdMaxEwaybill";
import validateOrderItemPartQuantityOrdersShouldShip from "./rule/validateOrderItemPartQuantityOrdersShouldShip";
import validateCannotShipTag from "./rule/validateCannotShipTag";
import validateManualOrderWaybillNo from "./rule/validateManualOrderWaybillNo";
import validateVirtualShip from "./rule/validateVirtualShip";
import validateDouDianRefundingOrder from "./rule/validateDouDianRefundingOrder";

interface ValidateShippingParams {
    orders: OrderDTO[];
    globalStore: typeof GlobalStore;
    /** 通用业务规则 */
    rule?: ShipValidationRule;
    /** 自定义校验规则（特殊发货场景使用） */
    validators?: ShipValidator[];
}

/**
 * 校验订单是否可以发货
 *
 * 功能说明：
 * 1. 按照预定义的规则顺序依次校验订单
 * 2. 如果某个校验规则失败，立即返回失败结果
 * 3. 每个校验规则可以过滤掉不符合条件的订单，将符合条件的订单传递给下一个规则
 * 4. 支持自定义校验规则，可以通过validators参数注入
 * 5. 如果所有规则都通过，返回成功结果
 * 6. 如果校验过程中出现异常，返回失败结果
 */
export async function validateShipping(params: ValidateShippingParams) {
    const { orders = [], globalStore, rule = {}, validators = [] } = params;
    const allValidators = [
        validatePrintShippingDisabled,
        validateAutoPrint,
        validateVirtualShip,
        validateDouDianRefundingOrder,
        validateRefundingOrder,
        validateJdMaxEwaybill,
        validateOrderItemPartQuantityOrdersShouldShip,
        validateCannotShipTag,
        validateManualOrderWaybillNo,
        ...validators,
    ];

    try {
        let canShipOrders = orders;

        // 遍历规则校验
        for (const validator of allValidators) {
            const result: ShipValidationResult = await validator(canShipOrders, rule, globalStore);

            if (!result.canShip) {
                return result; // 校验失败，提前退出
            }

            // 如果有过滤后的新订单，更新当前订单集合
            if (result.canShipOrders) {
                canShipOrders = result.canShipOrders;
            }
        }

        // 所有验证通过
        return buildValidationSuccessfulResult(canShipOrders);
    } catch (error) {
        console.log("校验失败:", error);

        return buildValidationFailedResult(error?.message);
    }
}
